import { Component } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { ErrorMessage } from '../../error-message.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ImageCropperComponent } from '../../../../partners/clinic-control-panel/components/image-cropper.component';

@Component({
    selector: 'formly-field-file',
    styles: [`
      .file-upload-zone {
        border: 2px dashed #dee2e6;
        border-radius: 8px;
        padding: 1.5rem;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        background: #f8f9fa;
      }

      .file-upload-zone:hover:not(.disabled) {
        border-color: var(--bs-primary);
        background: rgba(var(--bs-primary-rgb), 0.05);
      }

      .file-upload-zone.disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background: #e9ecef;
      }

      .file-upload-zone.has-error {
        border-color: var(--bs-danger);
        background: rgba(var(--bs-danger-rgb), 0.05);
      }

      .upload-icon {
        font-size: 2.5rem;
        color: var(--bs-primary);
        margin-bottom: 0.5rem;
      }

      .file-upload-zone.disabled .upload-icon {
        color: #6c757d;
      }

      .upload-text {
        font-size: 0.875rem;
        color: #6c757d;
        margin: 0;
      }

      .file-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem;
        background: #fff;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        transition: all 0.2s ease;
      }

      .file-item:hover {
        border-color: var(--bs-primary);
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      }

      .file-icon {
        width: 36px;
        height: 36px;
        border-radius: 6px;
        background: rgba(var(--bs-primary-rgb), 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .file-icon i {
        font-size: 1.25rem;
        color: var(--bs-primary);
      }

      .file-info {
        flex: 1;
        min-width: 0;
      }

      .file-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: #212529;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .file-size {
        font-size: 0.75rem;
        color: #6c757d;
        margin: 0;
      }

      .btn-remove {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border: none;
        background: transparent;
        color: #dc3545;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .btn-remove:hover {
        background: rgba(220, 53, 69, 0.1);
      }

      .files-grid {
        display: grid;
        gap: 0.5rem;
        margin-top: 1rem;
      }

      input[type='file'] {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0,0,0,0);
        white-space: nowrap;
        border: 0;
      }
    `],
    template: `
    <div class="mb-3">
      <label class="form-label" [ngClass]="{ 'fw-semibold': props.labelBold !== false }">
        {{ props.label }}
        <span *ngIf="props.required" class="text-danger">*</span>
      </label>

      <input
        #fileInput
        id="{{ id }}-file"
        type="file"
        [formControl]="control"
        [formlyAttributes]="field"
        [multiple]="props?.multiple && !isCoverImage"
        [required]="props?.required ?? false"
        [attr.accept]="acceptTypes"
        (change)="onFilesSelected($event)"
        [disabled]="maxFilesReached"
      />

      <div
        class="file-upload-zone"
        [class.disabled]="maxFilesReached"
        [class.has-error]="showError"
        (click)="!maxFilesReached && fileInput.click()"
      >
        <div class="upload-icon">
          <i class="bi" [class.bi-cloud-upload]="!maxFilesReached" [class.bi-check-circle]="maxFilesReached"></i>
        </div>
        <p class="upload-text fw-semibold mb-1">
          {{ maxFilesReached ? (translateService.instant('Maximum reached')) : (translateService.instant('Click to upload')) }}
        </p>
        <p class="upload-text" *ngIf="!maxFilesReached">
          {{ getAcceptedFormatsText() }}
          <span *ngIf="props?.maxFiles"> • {{ translateService.instant('Max') }} {{ props.maxFiles }} {{ translateService.instant('files') }}</span>
        </p>
      </div>

      <div class="files-grid" *ngIf="isCoverImage && croppedImageFile">
        <div class="file-item">
          <div class="file-icon">
            <i class="bi bi-image"></i>
          </div>
          <div class="file-info">
            <p class="file-name">{{ croppedImageFile.name }}</p>
            <p class="file-size">{{ formatFileSize(croppedImageFile.size) }}</p>
          </div>
          <button
            type="button"
            class="btn-remove"
            (click)="removeCoverImage()"
            [attr.aria-label]="translateService.instant('Remove file')"
            [title]="translateService.instant('Remove')"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>

      <div class="files-grid" *ngIf="files.length > 0 && !isCoverImage">
        <div class="file-item" *ngFor="let f of files; let i = index">
          <div class="file-icon">
            <i class="bi" [class.bi-image]="isImageFile(f)" [class.bi-file-earmark-pdf]="isPdfFile(f)" [class.bi-file-earmark]="!isImageFile(f) && !isPdfFile(f)"></i>
          </div>
          <div class="file-info">
            <p class="file-name">{{ f.name }}</p>
            <p class="file-size">{{ formatFileSize(f.size) }}</p>
          </div>
          <button
            type="button"
            class="btn-remove"
            (click)="removeFile(i)"
            [attr.aria-label]="translateService.instant('Remove file')"
            [title]="translateService.instant('Remove')"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>

      <div class="text-danger small mt-2" *ngIf="showError">
        <i class="bi bi-exclamation-circle me-1"></i>{{ getErrorMessage() }}
      </div>
    </div>
  `,
    standalone: false
})
export class FormlyFieldFile extends FieldType<any> {
  files: File[] = [];
  croppedImageUrl: string | null = null;
  croppedImageFile: File | null = null;

  get control() {
    return this.formControl as FormControl;
  }

  constructor(
    protected translateService: TranslateService,
    private dialog: MatDialog
  ) {
    super();
  }

  get isCoverImage(): boolean {
    return this.field.key === 'cover';
  }

  private exactOneFilesValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const files = Array.isArray(control.value) ? control.value : [];
      const isValid = files.length >= 1 && files.length <= 2;
      return isValid ? null : { oneOrTwoFiles: { actual: files.length } };
    };
  }

  get acceptTypes(): string {
    if (this.field.key === 'panoramicAttachments' || this.isCoverImage) {
      return 'image/*';
    }
    return this.props?.accept || 'image/*,application/pdf';
  }

  ngOnInit(): void {
    const existingValidators = this.control.validator
      ? [this.control.validator]
      : [];
    const validators = [...existingValidators];

    if (this.to.required) {
      validators.push(this.requiredFileValidator());
    }


    if (this.to.minFiles) {
      validators.push(this.minFilesValidator(this.to.minFiles));
    }

    if (this.field.key === 'panoramicAttachments') {
      validators.push(this.exactOneFilesValidator());
    }

    this.control.setValidators(validators);
    this.control.updateValueAndValidity();

    if (this.control.value && this.isCoverImage) {
      const existingFile = this.control.value;
      if (existingFile instanceof File) {
        this.croppedImageFile = existingFile;
        this.croppedImageUrl = URL.createObjectURL(existingFile);
      }
    }
  }

  private requiredFileValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (this.isCoverImage) {
        return value ? null : { required: true };
      }
      const files = Array.isArray(value) ? value : value ? [value] : [];
      return files.length > 0 ? null : { required: true };
    };
  }

  private minFilesValidator(min: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (this.isCoverImage) {
        return this.control.value
          ? null
          : { minFiles: { required: min, actual: 0 } };
      }
      const files = Array.isArray(control.value) ? control.value : [];
      return files.length >= min
        ? null
        : { minFiles: { required: min, actual: files.length } };
    };
  }

  get maxFilesReached(): boolean {
    const max = this.props?.maxFiles ?? Infinity;
    if (this.isCoverImage) {
      return this.croppedImageFile !== null;
    }
    return this.files.length >= max;
  }

  get labelText(): string {
    if (this.maxFilesReached) {
      return this.translateService.instant('Maximum reached');
    } else {
      if (this.isCoverImage && this.croppedImageFile) {
        return this.translateService.instant('Change image');
      }
      return this.translateService.instant('Choose file');
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  getAcceptedFormatsText(): string {
    if (this.field.key === 'panoramicAttachments' || this.isCoverImage) {
      return this.translateService.instant('Images only');
    }
    return this.translateService.instant('Images and PDF');
  }

  getErrorMessage(): string {
    if (!this.control.errors) {
      return '';
    }

    const files = Array.isArray(this.control.value) ? this.control.value : [];
    if (this.control.errors['oneOrTwoFiles']) {
      return this.translateService.instant('You must upload 1 photo at least');
    }

    const errorKey = Object.keys(this.control.errors)[0];
    if (errorKey) {
      const params = this.control.errors[errorKey];
      return ErrorMessage.getMessage(errorKey as any, params);
    }

    return '';
  }

  override get showError(): boolean {
    return (
      this.formControl.invalid &&
      (this.formControl.dirty || this.formControl.touched)
    );
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files || []);

    if (selectedFiles.length === 0) return;

    if (this.isCoverImage && selectedFiles[0].type.startsWith('image/')) {
      this.openImageCropper(selectedFiles[0]);
      input.value = '';
      return;
    }

    const maxFiles = this.to.maxFiles || Infinity;
    const combinedFiles = [...this.files, ...selectedFiles];
    const uniqueFiles = combinedFiles.filter(
      (file, index, self) =>
        index ===
        self.findIndex((f) => f.name === file.name && f.size === file.size)
    );

    this.files = uniqueFiles.slice(0, maxFiles);
    this.control.setValue(this.files);
    this.control.markAsTouched();
    this.control.markAsDirty();
    this.control.updateValueAndValidity();

    input.value = '';
  }

  openImageCropper(file: File) {
    const dialogRef = this.dialog.open(ImageCropperComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        imageFile: file,
        aspectRatio: 16 / 9,
        maintainAspectRatio: true,
        cropperMinWidth: 400,
        cropperMinHeight: 225,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.croppedFile) {
        if (this.croppedImageUrl) {
          URL.revokeObjectURL(this.croppedImageUrl);
        }

        this.croppedImageFile = result.croppedFile;
        this.croppedImageUrl = result.croppedImageUrl;

        this.control.setValue(this.croppedImageFile);
        this.control.markAsTouched();
        this.control.markAsDirty();
        this.control.updateValueAndValidity();
      }
    });
  }

  removeCoverImage() {
    if (this.croppedImageUrl) {
      URL.revokeObjectURL(this.croppedImageUrl);
    }
    this.croppedImageFile = null;
    this.croppedImageUrl = null;
    this.control.setValue(null);
    this.control.markAsTouched();
    this.control.markAsDirty();
    this.control.updateValueAndValidity();
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
    this.control.setValue(this.files);
    this.control.markAsTouched();
    this.control.markAsDirty();
    this.control.updateValueAndValidity();
  }

  ngOnDestroy() {
    if (this.croppedImageUrl) {
      URL.revokeObjectURL(this.croppedImageUrl);
    }
  }
}
